/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import { logGptCreate } from 'src/core/services/telemetry/eventsNamesTemplate';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getNextAbleResults } from 'src/core/utils/batchPromises';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { getCustomTextGptService } from 'src/customText/core/services/gpt/customTextGptService';
import { GptJobSwagger, GptPredictionDocument } from 'src/customText/core/services/gpt/customTextGptSwagger';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextEntityStore } from 'src/customText/core/stores/CustomTextEntityStore';
import {
    gptDocumentStoreToGptPredictedDocumentSwagger,
    gptDocumentSwaggerToStore,
    gptJobSwaggerToGptJobStore
} from 'src/customText/modules/handlers/customTextGptHandlerGroup/gptMappers';
import { getGptJobOperationHandler, isGptFeatureDisabled } from 'src/modules/gptPrediction/utils/gptUtils';

export type CustomTextGptHandlerGroup = ReturnType<typeof createCustomTextGptHandlerGroup>;

export const createCustomTextGptHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore, lifetime } = dependencies;
    const { serviceName } = getPortalFromUrl();

    const runGpt = async ({
        context,
        classes,
        extractors,
        documentsNames,
        azureOpenAiDeploymentName,
        azureOpenAiResourceRegion
    }: HandlerParams<{
        classes?: string[];
        extractors?: string[];
        documentsNames: string[];
        azureOpenAiResourceRegion: string;
        azureOpenAiDeploymentName: string;
    }>) => {
        if (isGptFeatureDisabled(rootStore.serviceKey.keyType, rootStore.azureStore.session.csAccountStore.region)) {
            return;
        }

        const { name: projectName, gptJobsStoreHouse, id: projectId } = rootStore.currentCustomTextProjectStore;
        const gptService = getCustomTextGptService({ context });

        const resultUrl = await gptService.runGptJob({
            classes,
            extractors,
            projectName,
            documentsNames,
            azureOpenAiDeploymentName,
            azureOpenAiResourceRegion,
            handler: getGptJobOperationHandler(lifetime, gptJobsStoreHouse)
        });

        logGptCreate(serviceName, projectId);

        await fetchGptPredictions({ resultUrl });
    };

    const fetchGptJobs = async ({ context }: HandlerParams<{}>) => {
        if (isGptFeatureDisabled(rootStore.serviceKey.keyType, rootStore.azureStore.session.csAccountStore.region)) {
            return;
        }

        const { name: projectName, gptJobsStoreHouse } = rootStore.currentCustomTextProjectStore;
        gptJobsStoreHouse.tracker.setState('running');
        const gptJobs = await getNextAbleResults<'jobs', GptJobSwagger>(
            (nextLink: string) => getCustomTextGptService({ context }).getGptJobs({ nextLink, projectName }),
            'jobs'
        );

        gptJobsStoreHouse.setItems(gptJobs.map(gptJobSwaggerToGptJobStore));
        gptJobsStoreHouse.tracker.setState('completed');

        const gptJobsInProgress = gptJobs
            .filter(j => j.status === 'notStarted' || j.status === 'running')
            .sort((j1, j2) => new Date(j2.createdDateTime).getMilliseconds() - new Date(j1.createdDateTime).getMilliseconds());

        if (gptJobsInProgress.length === 0) {
            return;
        }

        gptJobsInProgress.forEach(
            async job =>
                await getCustomTextGptService({ context }).pollGptJob({
                    projectName,
                    jobId: job.jobId,
                    handler: getGptJobOperationHandler(lifetime, gptJobsStoreHouse)
                })
        );
    };

    const fetchGptPredictions = async ({ context, resultUrl }: HandlerParams<{ resultUrl?: string }>) => {
        if (isGptFeatureDisabled(rootStore.serviceKey.keyType, rootStore.azureStore.session.csAccountStore.region)) {
            return;
        }

        const {
            documents,
            extractionStore,
            name: projectName,
            gptPredictionsStore,
            classificationStore
        } = rootStore.currentCustomTextProjectStore;
        gptPredictionsStore.tracker.setState('running');
        const gptResults = await getCustomTextGptService({ context }).getGptResults({ resultUrl, projectName });

        transaction(() => {
            gptPredictionsStore.setEntities(gptResults.entities?.map(e => new CustomTextEntityStore(e.category)) ?? []);
            gptPredictionsStore.setClasses(gptResults.classes?.map(c => new CustomTextClassStore({ name: c.category })) ?? []);
            gptPredictionsStore.setDocuments(
                gptResults.documents?.map(d =>
                    gptDocumentSwaggerToStore(d, extractionStore?.entities ?? [], classificationStore?.classes ?? [], documents)
                ) ?? []
            );
        });
        gptPredictionsStore.tracker.setState('completed');
    };

    const submitGptDecisions = async ({ context }: HandlerParams) => {
        if (isGptFeatureDisabled(rootStore.serviceKey.keyType, rootStore.azureStore.session.csAccountStore.region)) {
            return;
        }

        const { documents, projectKind, extractionStore, name: projectName, gptPredictionsStore } = rootStore.currentCustomTextProjectStore;
        await getCustomTextGptService({ context }).submitGptDecisions({
            projectName,
            projectKind,
            documents: gptPredictionsStore.documents
                .filter(
                    d =>
                        d.acceptedEntityLabels.length ||
                        d.rejectedEntityLabels.length ||
                        d.acceptedClassLabels.length ||
                        d.rejectedClassLabels.length
                )
                .map<GptPredictionDocument>(document => {
                    const currentDocument = documents.find(d => document.name === d.name);

                    return gptDocumentStoreToGptPredictedDocumentSwagger(
                        document,
                        currentDocument,
                        extractionStore?.entities ?? [],
                        projectKind
                    );
                })
        });

        transaction(() => {
            gptPredictionsStore.setIsDirty(false);
            gptPredictionsStore.setDocuments(
                gptPredictionsStore.documents
                    .filter(d => d.entityLabels.length || d.classLabels.length)
                    .map(d => {
                        d.setAcceptedClassLabels([]);
                        d.setRejectedClassLabels([]);
                        d.setAcceptedEntityLabels([]);
                        d.setRejectedEntityLabels([]);

                        return d;
                    })
            );
        });
    };

    return { runGpt, fetchGptJobs, fetchGptPredictions, submitGptDecisions };
};
